/**
 * Legacy activeEncounterId Migration
 *
 * CONTEXT:
 * Prior to RTK migration, activeEncounterId was stored directly in localStorage.
 * After RTK migration, it's stored in Redux state with persistence via redux-persist.
 *
 * This migration runs ONCE on app initialization to transfer the legacy value
 * to the new format, preventing loss of active draft references.
 *
 * CREATED: 2025-10-21
 * REMOVE_AFTER: 2025-11-20 (30 days after initial release)
 *
 * TODO(2025-11-20): Remove this migration after 30 days
 * - Delete this file: src/web-app/src/store/migrations/activeEncounterIdMigration.ts
 * - Remove import from src/web-app/src/store/index.ts
 * - Remove runActiveEncounterIdMigration() call from src/web-app/src/store/index.ts
 * - Optionally clean up legacy localStorage key with: localStorage.removeItem('activeEncounterId')
 *
 * Why 30 days?
 * - Gives users ample time to open the app at least once
 * - Covers typical sprint cycle for validation
 * - Industry standard for graceful data migrations
 */

import { setActiveWorkflowEncounter } from '@features/encounters';
import { selectWorkflowState } from '@features/encounters/store/selectors';
import * as Sentry from '@sentry/react';

import type { AppStore } from '../index';

const LEGACY_KEY = 'activeEncounterId';
const MIGRATION_FLAG_KEY = 'activeEncounterId_migrated';

/**
 * Run the legacy activeEncounterId migration
 *
 * This function:
 * 1. Checks if migration already ran (idempotent)
 * 2. Reads legacy localStorage value
 * 3. Validates it's a proper UUID
 * 4. Dispatches action to set in new Redux format
 * 5. Marks migration as complete
 * 6. KEEPS legacy value for rollback safety (remove after 30 days)
 *
 * @param store - Redux store instance
 */
export function runActiveEncounterIdMigration(store: AppStore): void {
  try {
    // Check if migration already ran
    const migrated = localStorage.getItem(MIGRATION_FLAG_KEY);
    if (migrated === 'true') {
      // Migration already completed - silent return
      return;
    }

    // Read legacy value
    const legacyActiveId = localStorage.getItem(LEGACY_KEY);

    if (!legacyActiveId) {
      // No legacy value to migrate - mark as complete
      localStorage.setItem(MIGRATION_FLAG_KEY, 'true');
      return;
    }

    // Validate UUID format (basic check)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidPattern.test(legacyActiveId)) {
      console.warn('[Migration] Legacy activeEncounterId is not a valid UUID:', legacyActiveId);
      localStorage.setItem(MIGRATION_FLAG_KEY, 'true');
      return;
    }

    // Check if new format already has a value (shouldn't happen, but safety check)
    const workflowState = selectWorkflowState(store.getState());
    const currentActiveId = workflowState?.activeWorkflowEncounterId;

    if (currentActiveId) {
      // New format already populated - skip migration
      localStorage.setItem(MIGRATION_FLAG_KEY, 'true');
      return;
    }

    // Dispatch action to set in new format
    store.dispatch(
      setActiveWorkflowEncounter({
        encounterId: legacyActiveId,
        reason: 'migrated-from-legacy',
      }),
    );

    // Mark migration as complete
    localStorage.setItem(MIGRATION_FLAG_KEY, 'true');

    // KEEP legacy value for rollback safety (don't remove yet)
    // TODO(2025-11-20): After 30 days, add: localStorage.removeItem(LEGACY_KEY);

    // eslint-disable-next-line no-console
    console.log(
      '✅ [Migration] Successfully migrated activeEncounterId to RTK format:',
      legacyActiveId,
    );

    // Log to analytics/monitoring if available
    Sentry.captureMessage('ActiveEncounterId migration completed', {
      level: 'info',
      tags: { migration: 'activeEncounterId' },
      extra: { encounterId: legacyActiveId },
    });
  } catch (error) {
    console.error('[Migration] Error during activeEncounterId migration:', error);

    // Log error but don't break app initialization
    Sentry.captureException(error, {
      tags: { migration: 'activeEncounterId' },
    });

    // Mark as complete even on error to avoid retry loops
    localStorage.setItem(MIGRATION_FLAG_KEY, 'true');
  }
}

/**
 * Utility function to check migration status (for debugging)
 * Available in browser console: window.__checkActiveEncounterMigration()
 */
export function checkMigrationStatus(): void {
  const legacyValue = localStorage.getItem(LEGACY_KEY);
  const migrated = localStorage.getItem(MIGRATION_FLAG_KEY);

  /* eslint-disable no-console */
  console.log('=== ActiveEncounterId Migration Status ===');
  console.log('Legacy value:', legacyValue || '(none)');
  console.log('Migration completed:', migrated === 'true' ? 'Yes' : 'No');
  console.log('Migration flag:', migrated);
  console.log('==========================================');
  /* eslint-enable no-console */
}

// Make debug function available in console
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
  (window as any).__checkActiveEncounterMigration = checkMigrationStatus;
}
